import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface Article {
  id: number;
  title_zh: string;
  title_en: string;
  summary_zh: string;
  summary_en: string;
  cover_img: string;
  read_time: string;
  published_at: string;
  featured: boolean;
  article_categories?: { name_zh: string };
}

const FALLBACK_IMGS = [
  'https://readdy.ai/api/search-image?query=IVF%20test%20tube%20embryo%20medical%20laboratory%20genetic%20screening%20microscope%2C%20blue%20tones%20medical%20knowledge%2C%20clean%20background%2C%20professional%20medical%20illustration%2C%20fertility%20science%20laboratory%20equipment&width=800&height=450&seq=kp-fb-001&orientation=landscape',
  'https://readdy.ai/api/search-image?query=pregnant%20woman%20prenatal%20care%20doctor%20consultation%2C%20warm%20clinic%20champagne%20gold%20tones%2C%20happy%20mother%20pregnancy%20medical%20care%2C%20modern%20fertility%20clinic%20interior&width=800&height=450&seq=kp-fb-002&orientation=landscape',
  'https://readdy.ai/api/search-image?query=legal%20document%20surrogacy%20contract%20law%20book%2C%20professional%20law%20office%2C%20blue%20navy%20tones%2C%20international%20family%20law%2C%20legal%20protection%20document%20signing&width=800&height=450&seq=kp-fb-003&orientation=landscape',
];

export default function KnowledgePreview() {
  const { t, i18n } = useTranslation();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const lang = (i18n.language === 'ru' ? 'ru' : i18n.language === 'en' ? 'en' : 'zh') as 'zh' | 'en' | 'ru';

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError('');
      try {
        // 优先展示「首页推荐」的文章，按权重（sort_order 越小越靠前）排序
        let { data, error: supaErr } = await supabase
          .from('articles')
          .select('id, title_zh, title_en, summary_zh, summary_en, cover_img, read_time, published_at, featured, article_categories(name_zh)')
          .eq('status', 'published')
          .lte('published_at', new Date().toISOString())
          .eq('featured', true)
          .order('sort_order', { ascending: true })
          .order('published_at', { ascending: false })
          .limit(4);
        if (supaErr) throw supaErr;
        // 没有推荐文章时，回退到最新发布
        if (!data || data.length === 0) {
          const fallback = await supabase
            .from('articles')
            .select('id, title_zh, title_en, summary_zh, summary_en, cover_img, read_time, published_at, featured, article_categories(name_zh)')
            .eq('status', 'published')
            .lte('published_at', new Date().toISOString())
            .order('published_at', { ascending: false })
            .limit(4);
          if (fallback.error) throw fallback.error;
          data = fallback.data;
        }
        setArticles(data ?? []);
      } catch (err: any) {
        setError(err?.message || '加载失败');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const getTitle = (a: Article) => {
    if (lang === 'en' && a.title_en) return a.title_en;
    return a.title_zh;
  };

  const getSummary = (a: Article) => {
    if (lang === 'en' && a.summary_en) return a.summary_en;
    return a.summary_zh;
  };

  const formatDate = (s: string) => new Date(s).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });

  if (loading) {
    return (
      <section className="py-20 md:py-28 bg-[#f8f5f0]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => <div key={i} className="bg-white rounded-2xl h-72 animate-pulse"></div>)}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 md:py-28 bg-[#f8f5f0]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <i className="ri-error-warning-line text-3xl text-red-300 mb-3 block"></i>
          <p className="text-red-500 text-sm mb-3">{error}</p>
          <button onClick={() => window.location.reload()} className="text-[#c9a96e] text-sm cursor-pointer">{t('retry')}</button>
        </div>
      </section>
    );
  }

  if (articles.length === 0) return null;

  const [featured, ...rest] = articles;

  return (
    <section className="py-20 md:py-28 bg-[#f8f5f0]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <div className="text-[#c9a96e] text-sm font-medium tracking-widest uppercase mb-2">{t('knowledge_section_label')}</div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c]">{t('knowledge_title')}</h2>
          </div>
          <Link to="/news" className="flex items-center gap-1 text-[#c9a96e] text-sm font-medium hover:gap-2 transition-all cursor-pointer whitespace-nowrap">
            {t('knowledge_view_all')} <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Featured + list */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Featured article */}
          <Link
            to={`/news/${featured.id}`}
            className="lg:col-span-1 group bg-white rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-1 transition-all duration-300 flex flex-col"
          >
            <div className="w-full h-56 overflow-hidden relative">
              <img
                src={featured.cover_img || FALLBACK_IMGS[0]}
                alt={getTitle(featured)}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-3 left-3">
                <span className="inline-block bg-[#1a3a5c]/80 text-[#c9a96e] text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm">
                  {featured.article_categories?.name_zh || ''}
                </span>
              </div>
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <h3 className="font-bold text-[#1a3a5c] text-sm leading-snug mb-2 line-clamp-3">{getTitle(featured)}</h3>
              <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-3 flex-1">{getSummary(featured)}</p>
              <div className="flex items-center gap-3 text-gray-400 text-xs mt-auto">
                <span>{featured.published_at ? formatDate(featured.published_at) : ''}</span>
                <span className="ml-auto flex items-center gap-0.5 text-[#c9a96e] group-hover:gap-1 transition-all">
                  {t('read_more')} <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </div>
            </div>
          </Link>

          {/* Article list */}
          <div className="lg:col-span-2 grid grid-cols-1 gap-4">
            {(rest.length > 0 ? rest : articles.slice(0, 3)).map((art, idx) => (
              <Link
                to={`/news/${art.id}`}
                key={art.id}
                className="group bg-white rounded-2xl overflow-hidden flex gap-0 cursor-pointer hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className="w-36 md:w-44 shrink-0 overflow-hidden relative" style={{ minHeight: '120px' }}>
                  <img
                    src={art.cover_img || FALLBACK_IMGS[idx % FALLBACK_IMGS.length]}
                    alt={getTitle(art)}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    style={{ minHeight: '120px' }}
                  />
                  <div className="absolute top-2 left-2">
                    <span className="inline-block bg-[#1a3a5c]/80 text-[#c9a96e] text-[10px] font-medium px-2 py-0.5 rounded-full backdrop-blur-sm whitespace-nowrap">
                      {art.article_categories?.name_zh || ''}
                    </span>
                  </div>
                </div>
                <div className="flex-1 p-3 md:p-4 flex flex-col justify-between">
                  <div>
                    <h3 className="font-bold text-[#1a3a5c] text-sm leading-snug mb-1.5 line-clamp-2">{getTitle(art)}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 hidden sm:block">{getSummary(art)}</p>
                  </div>
                  <div className="flex items-center gap-3 text-gray-400 text-xs mt-2">
                    <span>{art.published_at ? formatDate(art.published_at) : ''}</span>
                    <span className="ml-auto flex items-center gap-0.5 text-[#c9a96e] group-hover:gap-1 transition-all">
                      {t('read_more')} <i className="ri-arrow-right-line text-xs"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}